/* eslint-disable react/prop-types */
import {lazy, Suspense, useCallback, useContext, useEffect, useRef, useState} from 'react'
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {useUserConsents} from '@s-ui/consents'
import jsCookie from '@s-ui/js/lib/cookie'
import {useDecision} from '@s-ui/pde'
import Context from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import {useMount} from '@s-ui/react-hooks'
import ScriptJsonLD from '@s-ui/react-script-json-ld'
import {actionAdFavoriteTrackingOrigins} from '@adv-mt/mt-actions-ad-favorite'
import AdDisplay from '@adv-mt/mt-ad-display'
import ComparatorButton, {ComparatorDrawer, useComparator} from '@adv-mt/mt-comparator-button'
import DrawerAppSuggestion from '@adv-mt/mt-drawer-app-suggestion'
import EmptyCase, {emptyCaseDefaultSizes} from '@adv-mt/mt-empty-case-default'
import ErrorMessage from '@adv-mt/mt-error-message'
import {useAdSpaces} from '@adv-mt/mt-hook-use-ad-spaces'
import Layout, {layoutBasicBackgroundColors} from '@adv-mt/mt-layout-basic'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import ListAds from '@adv-mt/mt-list-ads'
import ListPagination, {pageNumberPlaceholder} from '@adv-mt/mt-list-pagination'
import MotorTagCloud from '@adv-mt/mt-list-tagcloud'
import LoginGoogleOneTapToGigyaConnector from '@adv-mt/mt-login-google-one-tap-to-gigya-connector'
import Breadcrumb from '@adv-mt/mt-navigation-breadcrumb'
import NetDrawer, {useReopenDrawerOnSession} from '@adv-mt/mt-net-drawer'
import NotificationsAdFavorite from '@adv-mt/mt-notifications-ad-favorite'
import NotificationsAdsComparator from '@adv-mt/mt-notifications-ads-comparator'
import NotificationAi from '@adv-mt/mt-notifications-ai'
import NotificationsSortManagement from '@adv-mt/mt-notifications-sort-management'
import PanelFaqs from '@adv-mt/mt-panel-faqs'
import PanelSearchFaqs from '@adv-mt/mt-panel-search-faqs'
import SearchAiTopBar, {searchAiTopBarPlaces} from '@adv-mt/mt-search-ai-top-bar'
import SearchSave, {searchSaveTrackingOrigins} from '@adv-mt/mt-search-save'
import SearchSidebar, {searchSidebarDesigns, searchSidebarPlaces} from '@adv-mt/mt-search-sidebar'
import SearchSort, {searchSortButtonDesigns, searchSortPlaces} from '@adv-mt/mt-search-sort'
import {useSearchTags} from '@adv-mt/mt-search-tags'
import SearchTopbar, {searchTopbarPlaces} from '@adv-mt/mt-search-topbar'
import SharedSmartbanner, {sharedSmartbannerPositions} from '@adv-mt/mt-shared-smartbanner'
import {sharedTopbarMaxContentWidths} from '@adv-mt/mt-shared-topbar'
import {spinnerBasicOverlayTypes} from '@adv-mt/mt-spinner-basic'
import TitleBasic, {
  titleBasicColors,
  titleBasicSizes,
  titleBasicTags,
  titleBasicTextAlign
} from '@adv-mt/mt-title-basic'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {criteoTrackPageEvents, useCriteoTrack} from '@adv-mt/mt-tracking-use-criteo'
import {useCsat} from '@adv-mt/mt-tracking-use-csat'

import LinkFactory from '../../components/LinkFactory/index.js'
import CsatNpsTracker from '../../helpers/csatNps/CsatNpsTracker.js'
import AssistantButton from './components/assistantButton/index.js'
import {CarConfiguratorAccess} from './components/CarConfiguratorAccess.js'
import {FaqsSection} from './components/FaqsSection/index.js'
import NetAccessBanner from './components/netAccessBanner/index.js'
import ComponentRecommender from './components/RecommenderCarousel.js'
import StickyButtons from './components/stickyButtons/index.js'
import SearchTagsBar from './experiments/searchTagsBar/index.js'
import {DiscardAdNotifications} from './DiscardAdNotifications.js'
import {NOTIFICATIONS, PAGINATION_PLACEHOLDER} from './getInitialProps.js'
import NotificationAdNotAvailable from './notificationAdNotAvailable.js'
import NotificationReservationAdNotAvailable from './notificationReservationAdNotAvailable.js'
import Skeleton from './Skeleton.js'
import tagcloudsWithPlaceholders from './tagcloudsWithPlaceholders.js'
import {
  createPaginationUrlFactory,
  getLastSearchTitle,
  getSearchFaqsTitle,
  mergeFavoritesResults,
  PAGE_ONE,
  scrollIntoViewVisitedAd
} from './utils.js'

import './styles/ui.css'

const {FullWidth: LayoutFullWidth} = Layout

const SpinnerBasic = lazy(() => import(/* webpackChunkName: "SpinnerBasic" */ '@adv-mt/mt-spinner-basic'))

const NotificationNoAlertMatches = lazy(() =>
  import(/* webpackChunkName: "NotificationNoAlertMatches" */ './notificationNoAlertMatches.js')
)

const FILTER_COUNT_CSAT_TRIGGER = 3
const FILTERS_RESTART_EVENT = 'Filters Restart Clicked'
const FILTERS_RESTART_ORIGIN = 'List Filters Restart Clicked'

const BASE_CLASS = 'mt-AdsList'
const COMPARATOR_NOTIFICATION_CLASS = `${BASE_CLASS}-comparatorNotification`

const AdsList = ({
  breadcrumb,
  configuratorData,
  configuratorEmptyCaseUrl,
  createdAlert,
  discardAd,
  error: hasServerError = false,
  faqsForUrlItems,
  faqsForUrlSectionTitle,
  filtersList,
  dynamicH1,
  hasNoMatchesUserAlertInformation,
  helmet,
  initialResults = {},
  initialSearch,
  isAdsListSearchPage,
  isLoading,
  isMakeHiredDealer,
  lastFilter,
  listFiltersOptions,
  listingSectionId,
  loadThirdParties,
  makeModelFaqs,
  notifications = {},
  pageErrors = {},
  pageName,
  paginationPlaceholderUrl,
  isRecommendedAdsFallback: isRecommendedAdsFallbackValue = false,
  recommendedAds: visitedAdRecommendedAds = {},
  reservationAdNotAvailableNotification,
  router,
  searchAiNotificationType,
  searchFaqs,
  showCardConfigurator,
  showNewVehicleFaq,
  showSortManagementNotification,
  staticH1,
  structuredDataJsonList = [],
  tagclouds,
  trackingData = {},
  visitedAdData = {}
}) => {
  const {domain, i18n} = useContext(Context)
  const {track, page} = useAnalyticsTrack()
  const {track: criteoTrack} = useCriteoTrack()
  const {trigger} = useCsat()
  const usedFiltersCount = useRef(0)
  const searchTopbarOverlayContentRef = useRef()
  const searchSidebarOverlayContentRef = useRef()

  // SSR compatibility - only show lazy components after hydration
  const [isMounted, setIsMounted] = useState(false)

  // React Router v7 hooks
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useMount(() => {
    setIsMounted(true)
  })
  const {pathname, search} = location

  const config = domain.get('config')
  const searchConfig = config.get('search')
  const appName = config.get('APP_NAME')
  const isCoches = appName === config.get('APP_NAME_COCHES')
  const isMobile = config.get('isMobile')
  const isTablet = config.get('isTablet')
  const {ADS_LIST_PAGE_NAME, ADS_LIST_VANS_AND_COMMERCIAL_PAGE_NAME} = config.get('page')
  const NET_ASSISTANT_PAGES = [ADS_LIST_PAGE_NAME, ADS_LIST_VANS_AND_COMMERCIAL_PAGE_NAME]
  const {CAMPAIGN_SUFFIX} = config.get('appSuggestion')

  const isDesktopOrWider = useMediaQuery(useMediaQueryBreakpoints.lg, {
    defaultMatches: !isMobile && !isTablet
  })

  // Client-side only flag to prevent lazy component rendering during SSR
  const isClient = typeof window !== 'undefined'
  const isNetAssistantEnabled = isCoches && NET_ASSISTANT_PAGES.includes(pageName)

  const adsListClassNames = cx(BASE_CLASS, `${BASE_CLASS}--grid`)

  useDecision('ff_web_pro_team_listing_relevance_incomming', {
    attributes: {page_name: pageName}
  })

  const {enabled: isTallAdThreeCards} = useDecision('ff_web_buyer_ads_grid_ad_3_cards')

  const {enabled: isAppSuggestionDrawerFlagEnabled} = useDecision('ff_web_growth_app_suggestion_drawer')
  const isAppSuggestionDrawerEnabled =
    isAppSuggestionDrawerFlagEnabled && isMobile && config.get('features').HAS_APP_SUGGESTION_DRAWER_ENABLED

  const {DRAWER_REOPEN_SESSION_KEY, NET_SUGGESTION_TYPE} = config.get('COMMON').AI_ASSISTANT
  const [recommendedAds, setRecommendedAds] = useState(visitedAdRecommendedAds)
  const [isNetOpen, setIsNetOpen] = useState(false)
  const [netSearchValue, setNetSearchValue] = useState(null)
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [searchInputValue, setSearchInputValue] = useState('')
  const [isComparatorOpen, setIsComparatorOpen] = useState(false)
  const [ads, setAds] = useState(initialResults.items)
  const netAccessBannerRef = useRef(null)

  // Sync results when the page is loaded or the search is changed to show the correct ads in the list
  useEffect(() => {
    setAds(initialResults.items)
  }, [initialResults.items])

  // Migrate locally-stored discarded ads to backend (temporary, fire-and-forget)
  useMount(() => {
    domain.get('sync_cookie_discarded_ads_use_case').execute()
  })

  // Update ads list with new favorites when an ad is marked/unmarker as favorite on net drawer
  const handleRefreshFavorites = useCallback(async favoriteAds => {
    setAds(prevAds => mergeFavoritesResults({vehicles: prevAds, favorites: favoriteAds}))
  }, [])

  const handleSearchBlur = useCallback(e => {
    if (netAccessBannerRef.current?.contains(e.relatedTarget)) return
    setIsSearchFocused(false)
  }, [])

  const hasRecommendedAds = Boolean(recommendedAds?.items?.length)

  const {categoryId, filteredOfferTypeId, offerTypeId, page: currentPage, vehicles = []} = initialSearch || {}
  const [firstSelectedVehicle] = vehicles

  const adSpaces = useAdSpaces({
    offerTypeId,
    initialSearch
  })
  const searchTags = useSearchTags({
    listingSectionId,
    pageName,
    search: initialSearch
  })

  const {GET_SEARCH_DATA_FROM_URL_ADS_ERROR} = config.get('ERRORS')
  const {ADS_LIST_TITLES} = config.get('seo')
  const {IS_GOT_ENABLED: isGotEnabled} = config.get('login')
  const {
    HIT_INFORMATIONS: {
      SMARTBANNER_POSITION_TOP: hitInformationSmartbannerPositionTop,
      NET_ASSISTANT_SEARCH_BAR_DROPDOWN
    },
    HIT_ORIGINS: {
      APP_SUGGESTION_DRAWER_ADS_LIST: hitOriginAppSuggestionDrawerAdsList,
      SMARTBANNER_ADS_LIST: hitOriginSmartbannerAdsList,
      LIST
    },
    TRACKING_EVENT_NAMES: {NET_ASSISTANT_ACCESS_CLICKED}
  } = config.get('tracking')

  const {
    CONSENTS: {LAST_ADS_SEARCH: lastAdsSearchRequiredConsents},
    COOKIES: {IS_VIEWED_SORT_MANAGEMENT_NOTIFICATION, VISITED_AD: visitedAdCookieKey},
    RECOMMENDED_ADS,
    SEARCH_AI
  } = config.get('COMMON')

  const {
    DEFAULT_SEARCH,
    VANS_AND_COMMERCIAL_CATEGORY1_ID,
    HAS_RECOMMENDER: isRecommenderActive,
    ENABLED_COMPARATOR_CATEGORY_IDS
  } = searchConfig

  // Comparator hook
  const {
    comparatorSelectedAds,
    comparatorNotificationType,
    handleSelectComparar,
    onRemoveSelectedAd,
    clearSelectedAds,
    resetNotification
  } = useComparator({
    pageName,
    origin: LIST,
    extendedTrackingData: trackingData.data
  })

  const hasAdsInComparatorMobile = comparatorSelectedAds?.length > 0 && !isDesktopOrWider
  const comparatorNotificationClassName = cx(COMPARATOR_NOTIFICATION_CLASS, {
    [`${COMPARATOR_NOTIFICATION_CLASS}--withBottomMargin`]: hasAdsInComparatorMobile && !isComparatorOpen,
    [`${COMPARATOR_NOTIFICATION_CLASS}--top`]: hasAdsInComparatorMobile && isComparatorOpen
  })

  const recommendedAdsConfig = RECOMMENDED_ADS.adsList
  const {minResultsToShowRecommendations, minRecommendations, maxRecommendations, visitedAdSessionStorageKey} =
    recommendedAdsConfig

  const isAiSearchActive = Boolean(SEARCH_AI.LISTING_SECTIONS_WITH_AI_SEARCH_AI.includes(listingSectionId))

  const {DEFAULT_CATEGORY1_ID, CARS_WITHOUT_LICENSE_CATEGORY1_ID} = config.get('search')
  const {CATEGORY_IDS_WITHOUT_ALERT_BUTTON} = config.get('searchFilters')

  const primaryCategories = [DEFAULT_CATEGORY1_ID, CARS_WITHOUT_LICENSE_CATEGORY1_ID, VANS_AND_COMMERCIAL_CATEGORY1_ID]
  const isPrimaryCategory = primaryCategories.includes(categoryId)

  const hasFiltersApplied = Boolean(searchTags.length)

  const isSearchSaveAvailable = !CATEGORY_IDS_WITHOUT_ALERT_BUTTON.includes(categoryId)

  const showSearchSave = hasFiltersApplied && isPrimaryCategory && isSearchSaveAvailable

  const STICKY_NOTIFICATION_CLASS = `${BASE_CLASS}-stickyNotification`
  const hasStickyBarMobile = (showSearchSave || comparatorSelectedAds?.length > 0) && !isDesktopOrWider
  const stickyNotificationClassName = cx(STICKY_NOTIFICATION_CLASS, {
    [`${STICKY_NOTIFICATION_CLASS}--withBottomMargin`]: hasStickyBarMobile && !isComparatorOpen
  })

  const hasConsentsForLastAdsSearch = useUserConsents(lastAdsSearchRequiredConsents)
  const serverErrorText = i18n.t(`ERROR_MESSAGE_SERVER_ERROR_${appName}_TEXT`)
  const {aggregations, totalPages, totalResults} = initialResults
  const hasResults = Boolean(ads.length)

  const {searchDataError, createAlertError} = pageErrors
  const hasPagination = totalPages > PAGE_ONE

  const firstResults = hasResults && ads.slice(0, 3).map(({id}) => id)

  const renderThirdParties = !isLoading && loadThirdParties
  const showAdNotAvailableNotification = Boolean(notifications[NOTIFICATIONS.AD_NOT_AVAILABLE])
  const [showReservationAdNotAvailableNotification, setShowReservationAdNotAvailableNotification] = useState(
    Boolean(reservationAdNotAvailableNotification)
  )

  const defaultSearch = {
    ...DEFAULT_SEARCH,
    categoryId,
    filteredOfferTypeId,
    offerTypeId
  }

  const isComparatorEnabledForPage = ENABLED_COMPARATOR_CATEGORY_IDS.includes(categoryId)

  const filtersConfig = filtersList?.map(filterName => ({
    component: '',
    name: filterName
  }))

  const searchTitle = totalResults ? dynamicH1 || `${i18n.n(totalResults)} ${staticH1}` : ''

  const showVansAndCommercialDescription = Boolean(categoryId === VANS_AND_COMMERCIAL_CATEGORY1_ID)

  const titleDescription = showVansAndCommercialDescription && i18n.t('COMMON.PROFESSIONAL_PRICE_WITHOUT_VAT_ADVICE')

  const showConfiguratorEmptyCase = Boolean(configuratorEmptyCaseUrl)

  const getEmptyCase = () => {
    const defaultEmptyCaseProps = {
      imageSize: emptyCaseDefaultSizes.LARGE,
      imageUrl: 'images/empty-cases/empty-case-inactive-dealers-coches.svg',
      text: 'EMPTY_CASE_NO_RESULTS_TEXT',
      title: 'EMPTY_CASE_NO_RESULTS_TITLE'
    }

    const configuratorEmptyCaseProps = {
      imageSize: emptyCaseDefaultSizes.LARGE,
      imageUrl: 'images/empty-cases/empty-case-new-vehicles-coches.png',
      text: i18n.t('EMPTY_CASE_CONFIGURATOR_TEXT', {
        make: firstSelectedVehicle?.make || i18n.t('COMMON.PLURALS.VEHICLE', 1).toLowerCase(),
        model: firstSelectedVehicle?.model?.name || ''
      }),
      title: 'EMPTY_CASE_CONFIGURATOR_TITLE',
      buttonHref: configuratorEmptyCaseUrl,
      buttonText: 'EMPTY_CASE_CONFIGURATOR_BUTTON_TEXT'
    }

    const emptyCaseProps = showConfiguratorEmptyCase ? configuratorEmptyCaseProps : defaultEmptyCaseProps

    return <EmptyCase {...emptyCaseProps} />
  }

  const handleCloseFilter = () => {}

  const handleShowSortManagementNotification = useCallback(
    () => jsCookie.set(IS_VIEWED_SORT_MANAGEMENT_NOTIFICATION, true),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleFilter = async ({search, lastFilter}) => {
    domain.get('get_url_params_search_use_case').execute({lastFilter, pageName, search, listingSectionId})
  }

  const handleOpenFilter = () => {}

  const handleRemoveFilters = () => {
    track({
      event: FILTERS_RESTART_EVENT,
      data: {
        hit_origin: FILTERS_RESTART_ORIGIN
      }
    })
    domain.get('get_url_params_search_use_case').execute({pageName, search: defaultSearch, listingSectionId})
  }

  const onOpenNet = searchValue => {
    setNetSearchValue(searchValue)
    setIsNetOpen(true)

    track({
      event: NET_ASSISTANT_ACCESS_CLICKED,
      data: {hit_origin: pageName, hit_information2: NET_ASSISTANT_SEARCH_BAR_DROPDOWN}
    })
  }

  const onCloseNet = () => {
    setNetSearchValue('')
    setIsNetOpen(false)
  }

  useEffect(() => {
    if (trackingData) page(trackingData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingData])

  useEffect(() => {
    domain.get('save_navigation_data_ads_use_case').execute({
      ads,
      currentPage,
      placeholder: PAGINATION_PLACEHOLDER,
      placeholderUrl: paginationPlaceholderUrl,
      searchUrl: `${pathname}${search}`,
      totalPages: initialResults.totalPages,
      totalResults: initialResults.totalResults
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ads, initialResults, currentPage])

  useReopenDrawerOnSession({
    isEnabled: isNetAssistantEnabled,
    storageKey: DRAWER_REOPEN_SESSION_KEY,
    onToggleDrawer: newStatus => setIsNetOpen(newStatus)
  })

  useMount(() => {
    const {unsubscribe} = domain.get('get_url_params_search_use_case').subscribe(({params, result}) => {
      const [, urlParams] = result
      usedFiltersCount.current += 1

      window.scrollTo(0, 0)
      // Navigate using React Router v7
      navigate(`${urlParams.path}${urlParams.query}`, {
        state: {
          lastFilter: params.lastFilter
        }
      })
    })
    return unsubscribe
  })

  useEffect(() => {
    // Get query params from React Router v7
    const query = Object.fromEntries(searchParams.entries())

    const {categoryId, filteredOfferTypeId, offerTypeId} = initialSearch || {}
    const params = {categoryId, filteredOfferTypeId, offerTypeId}

    const hasFilters = Boolean(Object.keys(query).length)
    const hasLastFilter = Boolean(lastFilter)

    const shouldSaveSearch = hasResults && hasFilters && hasLastFilter && hasConsentsForLastAdsSearch
    const hasTags = Boolean(searchTags.length)
    const title = hasTags
      ? getLastSearchTitle({
          categoryId,
          i18n,
          offerTypeId,
          searchConfig,
          tags: searchTags
        })
      : staticH1

    shouldSaveSearch &&
      domain.get('save_last_ads_search_use_case').execute({listingSectionId, params, pageName, query, title})
  }, [initialSearch, hasConsentsForLastAdsSearch]) // eslint-disable-line

  useEffect(() => {
    if (!config.get('features').HAS_CSAT_BUYER_ENABLED) return

    if (usedFiltersCount.current === FILTER_COUNT_CSAT_TRIGGER) {
      trigger('csat_buyer')
    }
  }, [initialSearch]) // eslint-disable-line

  useEffect(() => {
    criteoTrack({
      event: criteoTrackPageEvents.ADS_LIST,
      eventData: {adsListAdIds: firstResults}
    })
  }, [initialSearch]) // eslint-disable-line

  const isFewResultsPage = totalResults <= minResultsToShowRecommendations

  const showsVisitedAdRecommender = hasRecommendedAds && !isFewResultsPage && visitedAdData.isAccessible

  const recommenderTitle = i18n.t(
    !isMobile ? 'COMMON.RELATED_FROM_AD_VISUALIZATION_IN_GRID' : 'COMMON.RELATED_FROM_AD_VISUALIZATION'
  )

  const recommenderAdSpace = showsVisitedAdRecommender && {
    position: visitedAdData.position + 1,
    renderComponent: ({gridColumns}) => (
      <ComponentRecommender
        extendedTrackingData={trackingData.data}
        gridColumns={gridColumns}
        recommendedAds={recommendedAds.items}
        title={recommenderTitle}
        pageName={pageName}
      />
    )
  }

  // Recommended ads for few results page - showing recommendations at the bottom of the list
  const getRecommendedAds = async () => {
    const recommenderVisitedAdId = sessionStorage.getItem(visitedAdSessionStorageKey)
    if (!recommenderVisitedAdId) return {}

    const [recommendedAdsError, recommendedAdsResponse] = await domain
      .get('get_recommended_ads_use_case')
      .execute({adId: recommenderVisitedAdId, maxItems: maxRecommendations})

    if (recommendedAdsError) return {}
    if (recommendedAdsResponse.items.length >= minRecommendations) {
      setRecommendedAds(recommendedAdsResponse)
      if (recommendedAdsResponse.isRecommendedAdsFallback) {
        track({
          event: 'Recommended Ads Fallback',
          data: {
            hit_origin: pageName,
            hit_information: 'true'
          }
        })
      }
    }
  }

  useEffect(() => {
    if (isFewResultsPage && isRecommenderActive && !visitedAdData.id) {
      getRecommendedAds()
    }

    return () => {
      // Remove visited ad cookie when leaving the page here instead of initial props
      // to avoid removing it before the data is fetched a 2nd time if the page is refreshed
      if (isRecommenderActive && visitedAdData.id) {
        jsCookie.remove(visitedAdCookieKey)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFewResultsPage, isRecommenderActive, visitedAdData])

  useMount(() => {
    visitedAdData.isAccessible && scrollIntoViewVisitedAd({adId: visitedAdData.id, cookieKey: visitedAdCookieKey})
    if (isRecommendedAdsFallbackValue) {
      track({
        event: 'Recommended Ads Fallback',
        data: {
          hit_origin: pageName,
          hit_information: 'true'
        }
      })
    }
  })

  if (hasServerError || searchDataError instanceof GET_SEARCH_DATA_FROM_URL_ADS_ERROR) {
    return (
      <main className={BASE_CLASS}>
        <ErrorMessage text={serverErrorText} />
      </main>
    )
  }

  const isSearchFaqsShown = Boolean(searchFaqs)
  const showSpecificFaqsForUrl = Boolean(faqsForUrlItems && faqsForUrlItems.length)

  const isWebview = config.get('isWebview')

  const {SMARTBANNER_SOURCE_IDS} = config.get('smartbanner')

  const hasEntryOrMaxTermsFiltersApplied = Boolean(initialSearch?.entry) || Boolean(initialSearch?.maxTerms)

  return (
    <LayoutFullWidth
      linkFactory={LinkFactory}
      isMarginless
      isPaddingless
      bgColor={layoutBasicBackgroundColors.INHERIT}
      topbarProps={{
        maxContentWidth: sharedTopbarMaxContentWidths.EXTRA_LARGE,
        ...(isNetAssistantEnabled && {
          navCTASecondary: <AssistantButton isOpen={isNetOpen} setIsOpen={setIsNetOpen} pageName={pageName} />
        })
      }}
    >
      <CsatNpsTracker />
      {isClient && isMounted && isLoading && (
        <Suspense fallback={<></>}>
          <SpinnerBasic overlayType={spinnerBasicOverlayTypes.LIGHT} />
        </Suspense>
      )}
      {!isDesktopOrWider && (
        <div className="mt-AdsList-searchTopbar">
          {isAiSearchActive ? (
            <>
              <SearchAiTopBar
                aggregations={aggregations}
                filtersProps={listFiltersOptions}
                isLoading={isLoading}
                listingSectionId={listingSectionId}
                onInputChange={isNetAssistantEnabled ? setSearchInputValue : undefined}
                onSearchBlur={isNetAssistantEnabled ? handleSearchBlur : undefined}
                onSearchFocus={isNetAssistantEnabled ? () => setIsSearchFocused(true) : undefined}
                isSearchFocused={isSearchFocused}
                onOpenNet={onOpenNet}
                pageName={pageName}
                place={searchAiTopBarPlaces.ADS_LIST}
                search={initialSearch}
                totalResults={totalResults}
                withAiQuickFilters={true}
                showSearchSave={isSearchSaveAvailable}
              />
              {isNetAssistantEnabled && isSearchFocused && !searchInputValue && (
                <NetAccessBanner
                  ref={netAccessBannerRef}
                  onBlur={() => setIsSearchFocused(false)}
                  onClick={onOpenNet}
                />
              )}
            </>
          ) : (
            <SearchTopbar
              aggregations={aggregations}
              filtersConfig={filtersConfig}
              filtersProps={listFiltersOptions}
              hasFiltersApplied={hasFiltersApplied}
              isLoading={isLoading}
              listingSectionId={listingSectionId}
              onCloseFilter={handleCloseFilter}
              onFilter={handleFilter}
              onOpenFilter={handleOpenFilter}
              onRemoveFilters={handleRemoveFilters}
              overlayContentRef={searchTopbarOverlayContentRef}
              pageName={pageName}
              place={searchTopbarPlaces.ADS_LIST}
              search={initialSearch}
              showSearchSave={isSearchSaveAvailable}
              totalResults={totalResults}
            />
          )}
        </div>
      )}
      {isAppSuggestionDrawerEnabled ? (
        <DrawerAppSuggestion
          pageName={pageName}
          campaignSuffix={CAMPAIGN_SUFFIX}
          trackingOrigin={hitOriginAppSuggestionDrawerAdsList}
        />
      ) : (
        <SharedSmartbanner
          pageName={pageName}
          position={sharedSmartbannerPositions.STICKY}
          hasFiltersOffset={true}
          source={SMARTBANNER_SOURCE_IDS.ADS_LIST}
          trackingOrigin={hitOriginSmartbannerAdsList}
          trackingInformation={hitInformationSmartbannerPositionTop}
        />
      )}
      {hasStickyBarMobile && (
        <StickyButtons
          showSearchSave={showSearchSave}
          createdAlert={createdAlert}
          initialSearch={initialSearch}
          isNetOpen={isNetOpen}
          setIsNetOpen={setIsNetOpen}
          comparatorSelectedAds={comparatorSelectedAds}
          isComparatorOpen={isComparatorOpen}
          onToggleComparator={setIsComparatorOpen}
        />
      )}
      <main className={adsListClassNames} ref={searchTopbarOverlayContentRef}>
        {hasResults && (
          <div className="mt-AdsList-titleHeader">
            <div className="mt-AdsList-title">
              <TitleBasic
                as={titleBasicTags.h1}
                description={titleDescription}
                descriptionTextAlign={isDesktopOrWider ? titleBasicTextAlign.left : titleBasicTextAlign.center}
                size={isDesktopOrWider ? titleBasicSizes.xs : titleBasicSizes.xxs}
                textAlign={isDesktopOrWider ? titleBasicTextAlign.left : titleBasicTextAlign.center}
                title={searchTitle}
              />
            </div>
            {isDesktopOrWider || (!isDesktopOrWider && !isAiSearchActive) ? (
              <SearchSort
                design={searchSortButtonDesigns.BASIC}
                linkFactory={LinkFactory}
                listingSectionId={listingSectionId}
                pageName={pageName}
                place={searchSortPlaces.ADS_LIST}
                search={initialSearch}
                withSortOrderInfo
              />
            ) : (
              showSortManagementNotification && (
                <NotificationsSortManagement onClose={handleShowSortManagementNotification} />
              )
            )}
          </div>
        )}
        {isDesktopOrWider && (
          <SearchTagsBar
            pageName={pageName}
            showSearchSave={isSearchSaveAvailable}
            createdAlert={createdAlert}
            listingSectionId={listingSectionId}
            search={initialSearch}
            place={searchSidebarPlaces.ADS_LIST}
            searchSaveTrackingOrigin={searchSaveTrackingOrigins.ADS_LIST}
          />
        )}
        <div className="mt-AdsList-inner">
          {isDesktopOrWider && (
            <div className="mt-AdsList-sidebar">
              <SearchSidebar
                aggregations={aggregations}
                createdAlert={createdAlert}
                design={searchSidebarDesigns.WRAPPED}
                filtersProps={listFiltersOptions}
                isFullWidth
                isLoading={isLoading}
                listingSectionId={listingSectionId}
                overlayContentRef={searchSidebarOverlayContentRef}
                pageName={pageName}
                place={searchSidebarPlaces.ADS_LIST}
                search={initialSearch}
                searchSaveTrackingOrigin={searchSaveTrackingOrigins.ADS_LIST}
                withLastSavedSearches
                withAiQuickFilters={isAiSearchActive}
              />
            </div>
          )}
          <section className="mt-AdsList-content" ref={searchSidebarOverlayContentRef}>
            {isClient && isMounted && hasNoMatchesUserAlertInformation && (
              <Suspense fallback={<></>}>
                <NotificationNoAlertMatches />
              </Suspense>
            )}
            {hasResults ? (
              <>
                {showAdNotAvailableNotification && (
                  <div className="mt-AdsList-notifications">
                    <NotificationAdNotAvailable />
                  </div>
                )}
                {showReservationAdNotAvailableNotification && (
                  <div className="mt-AdsList-notifications">
                    <NotificationReservationAdNotAvailable
                      onClose={() => setShowReservationAdNotAvailableNotification(false)}
                    />
                  </div>
                )}
                <ListAds
                  ads={ads}
                  adSpaces={recommenderAdSpace ? [...adSpaces, recommenderAdSpace] : adSpaces}
                  count={ads.length}
                  currentPage={currentPage}
                  extendedTrackingData={trackingData.data}
                  favoriteTrackingOrigin={actionAdFavoriteTrackingOrigins.LIST_AD_FAVORITED}
                  hasDiscardButton
                  hasEntryOrMaxTermsFiltersApplied={hasEntryOrMaxTermsFiltersApplied}
                  hasMenu
                  isLoading={isLoading}
                  linkFactory={LinkFactory}
                  pageName={pageName}
                  withAdViewedTracking
                  enableComparator={isComparatorEnabledForPage}
                  comparatorSelectedAds={comparatorSelectedAds}
                  handleSelectComparar={handleSelectComparar}
                  isGridLayout
                />
                {showCardConfigurator && (
                  <CarConfiguratorAccess
                    filterVehicle={firstSelectedVehicle}
                    configuratorData={configuratorData}
                    hasResults={hasResults}
                  />
                )}
                {hasRecommendedAds && isFewResultsPage && (
                  <ComponentRecommender
                    extendedTrackingData={trackingData.data}
                    recommendedAds={recommendedAds.items}
                    title={i18n.t('COMMON.RELATED_VISUALIZATION')}
                    pageName={pageName}
                  />
                )}
                {hasPagination && (
                  <div className="mt-AdsList-pagination">
                    <ListPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      linkFactory={LinkFactory}
                      urlPattern={paginationPlaceholderUrl}
                      createUrl={createPaginationUrlFactory(pageNumberPlaceholder)}
                    />
                  </div>
                )}
                {!showCardConfigurator && showConfiguratorEmptyCase && getEmptyCase()}
              </>
            ) : (
              <>
                {showCardConfigurator && (
                  <CarConfiguratorAccess
                    filterVehicle={firstSelectedVehicle}
                    configuratorData={configuratorData}
                    hasResults={hasResults}
                  />
                )}
                {!showCardConfigurator && getEmptyCase()}
                {hasRecommendedAds && (
                  <ComponentRecommender
                    extendedTrackingData={trackingData.data}
                    recommendedAds={recommendedAds.items}
                    title={i18n.t('COMMON.RELATED_VISUALIZATION')}
                    pageName={pageName}
                  />
                )}
              </>
            )}
          </section>
        </div>

        {showSpecificFaqsForUrl && (
          <section className="mt-AdsList-faqsForUrl">
            <TitleBasic as={titleBasicTags.h3} title={faqsForUrlSectionTitle} size={titleBasicSizes.s} />
            <PanelFaqs questions={faqsForUrlItems} />
          </section>
        )}

        {!isAdsListSearchPage && isSearchFaqsShown && (
          <section className="mt-AdsList-searchFaqs">
            <div className="mt-AdsList-searchFaqsTitle">
              <TitleBasic
                as={titleBasicTags.h2}
                color={titleBasicColors.black}
                size={titleBasicSizes.s}
                title={getSearchFaqsTitle({semanticDescriptor: staticH1, i18n})}
              />
            </div>
            <PanelSearchFaqs searchFaqs={searchFaqs} semanticDescriptor={staticH1} />
          </section>
        )}
        {!isAdsListSearchPage && showNewVehicleFaq && (
          <FaqsSection
            make={firstSelectedVehicle?.make}
            model={firstSelectedVehicle?.model?.name}
            makeModelFaqs={makeModelFaqs}
          />
        )}
        {!isAdsListSearchPage && (
          <div className="mt-AdsList-tagcloudsWrapper">
            <TitleBasic as={titleBasicTags.h3} title={i18n.t('COMMON.RELATED_SEARCH')} size={titleBasicSizes.s} />
            <MotorTagCloud
              initialTagclouds={tagcloudsWithPlaceholders({
                adsListTypesConfig: ADS_LIST_TITLES,
                tagclouds,
                initialSearch,
                i18n
              })}
              pageName={pageName}
              linkFactory={LinkFactory}
            />
          </div>
        )}
        {!isAdsListSearchPage && <Breadcrumb items={breadcrumb} linkFactory={LinkFactory} />}

        <NetDrawer
          pageName={pageName}
          listingSectionId={listingSectionId}
          isOpen={isNetOpen}
          onClose={onCloseNet}
          onOpen={onOpenNet}
          onFavoriteChange={handleRefreshFavorites}
          initialSearchValue={netSearchValue}
          context={{page: {type: NET_SUGGESTION_TYPE.AD_LISTING}}}
        />
        <ComparatorDrawer
          isOpen={isComparatorOpen}
          onClose={() => setIsComparatorOpen(false)}
          comparatorSelectedAds={comparatorSelectedAds}
          onRemoveSelectedAd={onRemoveSelectedAd}
          clearSelectedAds={clearSelectedAds}
          extendedTrackingData={trackingData.data}
          closeOnOutsideClick={!comparatorNotificationType}
        />
      </main>
      <NotificationsAdFavorite className={stickyNotificationClassName} />
      <NotificationsAdsComparator
        type={comparatorNotificationType}
        onClose={resetNotification}
        comparatorSelectedAds={comparatorSelectedAds}
        extendedTrackingData={trackingData.data}
        className={comparatorNotificationClassName}
      />
      {createAlertError && (
        <SearchSave.ErrorNotification show={createAlertError} className={stickyNotificationClassName} />
      )}
      <DiscardAdNotifications showNotification={Boolean(discardAd)} className={stickyNotificationClassName} />
      <Helmet {...helmet} />
      {structuredDataJsonList.map((structuredData, index) => (
        <ScriptJsonLD json={structuredData} key={index} />
      ))}
      {isGotEnabled && !isWebview && <LoginGoogleOneTapToGigyaConnector />}
      {renderThirdParties && (
        <AdDisplay
          pageName={pageName}
          search={initialSearch}
          experimentFlags={[`ff_web_buyer_ads_grid_ad_3_cards-${isTallAdThreeCards ? 'on' : 'off'}`]}
        />
      )}
      {comparatorSelectedAds.length > 0 && isDesktopOrWider && (
        <div className={`${BASE_CLASS}-comparatorButton`}>
          <ComparatorButton
            comparatorSelectedAds={comparatorSelectedAds}
            isOpen={isComparatorOpen}
            onToggle={setIsComparatorOpen}
          />
        </div>
      )}
      {searchAiNotificationType && <NotificationAi type={searchAiNotificationType} />}
    </LayoutFullWidth>
  )
}

AdsList.displayName = 'AdsList'

AdsList.propTypes = {
  breadcrumb: PropTypes.array,
  configuratorEmptyCaseUrl: PropTypes.string,
  extendedTagclouds: PropTypes.array,
  hasNoMatchesUserAlertInformation: PropTypes.bool,
  dynamicH1: PropTypes.string,
  createdAlert: PropTypes.object,
  helmet: PropTypes.object,
  initialSearch: PropTypes.object,
  lastFilter: PropTypes.string,
  listFiltersOptions: PropTypes.object,
  initialResults: PropTypes.shape({
    items: PropTypes.array,
    totalResults: PropTypes.number,
    totalPages: PropTypes.number
  }),
  isLoading: PropTypes.bool,
  loadThirdParties: PropTypes.bool,
  notifications: PropTypes.object,
  pageErrors: PropTypes.object,
  pageName: PropTypes.string,
  paginationPlaceholderUrl: PropTypes.string,
  router: PropTypes.object,
  searchFaqs: PropTypes.array,
  staticH1: PropTypes.string,
  structuredDataJsonList: PropTypes.array,
  tagclouds: PropTypes.array
}

// this element helps avoid poor CLS performance on Detail page when on SPA
AdsList.renderLoading = () => <Skeleton />

// It keeps page mounted in order to avoid flickering when user filters
AdsList.keepMounted = true

export default AdsList
